import { useEffect, useLayoutEffect, useState, DependencyList, useContext } from 'react';
import queryString from 'query-string';
import { UserContext } from './pages/auth/UserProvider';
import {
  handleAdobePageAnalytics,
  QueryParamToCookieOptions,
  setCookiesFromQueryParams,
  getCookie,
  unsetCookie,
  setCookie,
} from './utils';
import { History, Location } from 'history';
import { getPartner, usePartner } from 'packages/smart-home-support/partner';
import { getQueryString, buildQuery, buildSplitQuery, updateQueryString } from './pages/calculator/utils';
import { Devices, DeviceQuantities } from './pages/calculator/models';
import { CookieName } from './constants';
import { CommonEventApiClient } from '@soluto-private/common-event-api-client';
import { name as clientApplicationName, version as clientApplicationVersion } from '../../package.json';
import config from 'packages/web-app/src/config';

export const useCancelableEffect: typeof useEffect = (effect, deps?) => {
  return useEffect(() => {
    let didCancel = false;
    if (!didCancel) {
      effect();
    }
    return () => {
      didCancel = true;
    };
  }, deps);
};

export const useScrollResetter = (deps: DependencyList = []) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, deps);
};

export const useAdobeAnalytics = () => {
  useEffect(() => {
    handleAdobePageAnalytics();
  }, []);
};

export const useQueryParamsAsCookies = (queryParamToCookieOptions: QueryParamToCookieOptions[], history: History) => {
  useEffect(() => {
    setCookiesFromQueryParams(queryParamToCookieOptions, history);
  }, []);
};

export const useTitle = (title?: string) => {
  const { websiteName } = getPartner();
  useEffect(() => {
    document.title = (title ? `${title} | ` : '') + websiteName;
  }, []);
};

export const useDevicesFromQueryString = (location: Location, devices: Devices): {} => {
  const [selectedDevices, setSelectedDevices] = useState({});

  useEffect(() => {
    const query = getQueryString(location);
    setSelectedDevices(buildQuery(query, devices));
  }, [location]);

  return selectedDevices;
};

export const useQueryParameterDevices = (devices: Devices, location: Location, history: History): [{}, Function] => {
  const params: {} = getQueryString(location);

  const [categorizedQuery, setCategorizedQuery] = useState(buildSplitQuery(params, devices));

  const setDeviceQuery = (updatedDeviceCount: DeviceQuantities) =>
    setCategorizedQuery({
      Devices: { ...categorizedQuery.Devices, ...updatedDeviceCount },
      NonDevices: categorizedQuery.NonDevices,
    });

  useEffect(() => {
    updateQueryString(history, { ...categorizedQuery.Devices, ...categorizedQuery.NonDevices });
  }, [categorizedQuery]);

  return [categorizedQuery.Devices, setDeviceQuery];
};

export const useToast = (duration: number = 5): [typeof isShowing, typeof trigger] => {
  const [shouldShow, setShouldShow] = useState(false);
  const [isShowing, setIsShowing] = useState(false);
  const trigger = () => setShouldShow(true);
  const durationMs = duration * 1000;

  useEffect(() => {
    setIsShowing(shouldShow);

    if (!shouldShow) {
      return;
    }

    const timer = setTimeout(() => {
      setIsShowing(false);
      setShouldShow(false);
    }, durationMs);

    return () => {
      clearTimeout(timer);
    };
  }, [shouldShow]);

  return [isShowing, trigger];
};

type QueryParamTransformer<T> = (value: string | undefined) => T;
type SetQueryParamValue<T> = (value: T | undefined, history?: History, push?: boolean) => void;
type UseQueryParamTuple<T> = [T, SetQueryParamValue<T>];

export function useQueryParam(key: string): UseQueryParamTuple<string | undefined>;
export function useQueryParam<T>(key: string, transform: QueryParamTransformer<T>): UseQueryParamTuple<T>;
export function useQueryParam<T>(
  key: string,
  transform: QueryParamTransformer<T>,
  untransform: (value: T) => string | undefined
): UseQueryParamTuple<T>;
export function useQueryParam(
  key: string,
  transform?: QueryParamTransformer<any>,
  untransform?: (value: any) => string | undefined
): UseQueryParamTuple<any> {
  transform = transform || ((v) => v);
  const [value, setValue] = useState<any>(
    transform(queryString.parse(window.location ? window.location.search : '')[key])
  );

  return [
    value,
    (value: any, history?: History, push?: boolean) => {
      setValue(value);
      if (history) {
        if (value === undefined) {
          const query = queryString.parse(history.location.search);
          delete query[key];
          if (push) {
            history.push({ search: queryString.stringify(query), hash: history.location.hash });
          } else {
            history.replace({ search: queryString.stringify(query), hash: history.location.hash });
          }
        } else {
          const stringVal = untransform ? untransform(value) : String(value);
          const search = queryString.stringify({ ...queryString.parse(history.location.search), [key]: stringVal });
          if (push) {
            history.push({ search, hash: history.location.hash });
          } else {
            history.replace({ search, hash: history.location.hash });
          }
        }
      }
    },
  ];
}

const getPageScrollPosition = () => {
  const doc = document.documentElement;
  return (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
};

export const usePageScrollPosition = (): typeof pageScrollPosition => {
  const [pageScrollPosition, setPageScrollPosition] = useState(getPageScrollPosition());
  const listener = () => setPageScrollPosition(getPageScrollPosition());
  useLayoutEffect(() => {
    listener();
    window.addEventListener('scroll', listener);
    window.addEventListener('wheel', listener);
    return () => {
      window.removeEventListener('scroll', listener);
      window.removeEventListener('wheel', listener);
    };
  }, []);
  return pageScrollPosition;
};

const getWindowDimensions = (): [number, number] => [window.innerWidth, window.innerHeight];

export const useWindowDimensions = (debounce: number = 250): typeof dimensions => {
  const [dimensions, setDimensions] = useState<[number, number]>([0, 0]);
  useLayoutEffect(() => {
    let resizeTimer: any | undefined = undefined;

    const listener = () => {
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
      resizeTimer = setTimeout(() => {
        setDimensions(getWindowDimensions());
      }, debounce);
    };

    listener();

    window.addEventListener('resize', listener);
    return () => {
      window.removeEventListener('resize', listener);
    };
  }, []);
  return dimensions;
};

export const useUser = () => useContext(UserContext).user;

export const useContactPageEventing = () => {
  const [partner] = usePartner();
  const agentId: string | undefined = getCookie(CookieName.AgentId);
  const saleSessionId: string | undefined = getCookie(CookieName.SaleSessionId);
  const sourceApp: string | undefined = getCookie(CookieName.SourceApp);
  let openedLinkForExpert = getCookie(CookieName.OpenedLinkForExpert);

  useEffect(() => {
    if (openedLinkForExpert && openedLinkForExpert !== agentId) {
      unsetCookie(CookieName.OpenedLinkForExpert);
      openedLinkForExpert = undefined;
    }

    const emitEnrollmentStartedEvent = async () => {
      if (agentId && saleSessionId && !openedLinkForExpert) {
        const commonEventClient = new CommonEventApiClient({
          clientApplicationName,
          clientApplicationVersion,
          url: config.COMMONEVENT_KINESIS_URL,
        });

        await commonEventClient.sendEvent({
          type: 'SmartHome',
          subType: 'HorizonEnrollmentProgress',
          partner: partner.partnerName,
          data: {
            expertId: agentId,
            sessionId: saleSessionId,
            status: 'LinkOpened',
            sourceApp,
          },
          dateTime: new Date().toISOString(),
        });

        // set cookie property that event has been fired so it does not fire again
        setCookie(CookieName.OpenedLinkForExpert, agentId, { expires: 7 });
      }
    };

    emitEnrollmentStartedEvent();
  }, [agentId, saleSessionId]);
};



// WEBPACK FOOTER //
// ./packages/smart-home-support/hooks.tsx